import { isolateForPlainText } from "@a2z/ui";
import type { useTranslate } from "@/lib/i18n";

type Translate = ReturnType<typeof useTranslate>;

/**
 * A price tier's happy-hour window, in words (D47, B4).
 *
 * Shared by the till's tier picker and the back-office tiers list because both
 * ask the same question of the same three columns — "when does this apply?" —
 * and answering it twice is how the two screens end up disagreeing about a
 * midnight wrap.
 *
 * What this file deliberately does NOT do is decide whether a tier is active
 * RIGHT NOW. That verdict is the server's (`isActiveNow` on the till's tier
 * read), computed against the STORE's timezone. A browser knows the waiter's
 * phone's timezone, which in a chain is frequently not the restaurant's, and a
 * picker that computed its own answer would enable a tap the open command then
 * refuses with a typed 400. These functions turn stored minutes into hours a
 * human can read; nothing here is a gate.
 */

/**
 * The bitmask's own order: bit0 = Monday … bit6 = Sunday, matching
 * `policies/price-tier-window.policy.ts`. NOT the locale's week order — this
 * is the storage layout, and the labels below are indexed by it.
 */
export const WEEKDAY_BIT_ORDER = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;

export type WeekdayKey = (typeof WEEKDAY_BIT_ORDER)[number];

/**
 * The order a week is READ in, per locale — never the order it is stored in.
 *
 * Egypt's week starts Saturday and its weekend is Friday–Saturday, so a report
 * whose rows start on Monday puts a restaurant's two biggest days at opposite
 * ends of the grid. This is a DISPLAY order and returns storage indices
 * (`WEEKDAY_BIT_ORDER` positions, bit0 = Monday), which is the whole point:
 * reordering `WEEKDAY_BIT_ORDER` itself would silently rewrite the meaning of
 * every price-tier day mask already saved in the database — a pricing
 * migration wearing a reports change as a disguise.
 *
 * English keeps Monday-first: the Arabic side is the primary language and gets
 * the local week, and an English reader of the same screen is almost always
 * the same person or their accountant, for whom Monday-first is the convention
 * the rest of the product's exports already use.
 */
const WEEKDAY_DISPLAY_ORDER: Record<"ar" | "en", readonly number[]> = {
  // Sat, Sun, Mon, Tue, Wed, Thu, Fri — as bit indices.
  ar: [5, 6, 0, 1, 2, 3, 4],
  en: [0, 1, 2, 3, 4, 5, 6],
};

/**
 * Storage indices in the order this locale reads a week.
 *
 * Callers index both their data (`grid[weekdayIndex]`) and the labels
 * (`WEEKDAY_BIT_ORDER[weekdayIndex]`) through the returned numbers, so a
 * reordered screen can never pair Saturday's row with Monday's figures.
 */
export function weekdayDisplayOrder(locale: string): readonly number[] {
  return WEEKDAY_DISPLAY_ORDER[locale === "ar" ? "ar" : "en"];
}

/** Every day selected — the same thing as `null`, said with all seven bits. */
export const ALL_DAYS_MASK = 127;

export const MINUTES_PER_DAY = 1440;

export function weekdayLabel(t: Translate, day: WeekdayKey): string {
  switch (day) {
    case "mon":
      return t((m) => m.manage.weekdayMon);
    case "tue":
      return t((m) => m.manage.weekdayTue);
    case "wed":
      return t((m) => m.manage.weekdayWed);
    case "thu":
      return t((m) => m.manage.weekdayThu);
    case "fri":
      return t((m) => m.manage.weekdayFri);
    case "sat":
      return t((m) => m.manage.weekdaySat);
    case "sun":
      return t((m) => m.manage.weekdaySun);
  }
}

export function isDaySelected(mask: number | null, index: number): boolean {
  // `null` means every day, so an unset mask reads as all seven ticked rather
  // than none — the form must not present "applies every day" as an empty
  // choice a manager then has to fill in to keep what they already had.
  if (mask === null) return true;
  return (mask & (1 << index)) !== 0;
}

export function toggleDayInMask(mask: number | null, index: number): number {
  const current = mask === null ? ALL_DAYS_MASK : mask;
  return current ^ (1 << index);
}

/**
 * `540` → `"09:00"`. Always 24-hour and always zero-padded, in Latin digits in
 * both locales: this is a clock reading on a chip beside a discount, and an
 * Arabic-indic 24-hour time next to a Latin percentage reads as two different
 * facts from two different systems.
 */
export function formatMinuteOfDay(minute: number): string {
  const normalised = ((minute % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalised / 60);
  const minutes = normalised % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** `"09:00"` → `540`, or `null` for anything that is not a real clock time. */
export function parseMinuteOfDay(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export type TierWindow = {
  activeDays: number | null;
  activeFromMinute: number | null;
  activeToMinute: number | null;
};

/** True when the tier applies at every hour of every day — no window at all. */
export function isAlwaysOn(window: TierWindow): boolean {
  return (
    (window.activeDays === null || window.activeDays === ALL_DAYS_MASK) &&
    window.activeFromMinute === null &&
    window.activeToMinute === null
  );
}

export function formatDayMask(t: Translate, mask: number | null): string {
  if (mask === null || mask === ALL_DAYS_MASK) {
    return t((m) => m.manage.tierWindowEveryDay);
  }
  const days = WEEKDAY_BIT_ORDER.filter((_, index) =>
    isDaySelected(mask, index),
  ).map((day) => weekdayLabel(t, day));
  // A mask of 0 cannot be stored (the DTO's `@Min(1)` and a DB CHECK both
  // refuse it), but a row that predates them, or a hand-written one, would
  // otherwise render an empty string where the days go.
  return days.length > 0 ? days.join(" · ") : t((m) => m.manage.tierWindowNoDay);
}

/**
 * The whole window on one line, or `null` when there is no window to state.
 *
 * `null` is the answer the CALLER should render as nothing at all rather than
 * as "always": a chip saying "every day, all day" beside every ordinary tier is
 * noise on the one screen where the tiers that DO have hours need to stand out.
 */
export function formatTierWindow(
  t: Translate,
  window: TierWindow,
): string | null {
  if (isAlwaysOn(window)) return null;
  const days = formatDayMask(t, window.activeDays);
  if (window.activeFromMinute === null || window.activeToMinute === null) {
    return days;
  }
  /*
   * The range is ISOLATED as one unit, dash included.
   *
   * This function returns a plain STRING, and its callers drop it into Arabic
   * prose (the tiers list, the till's tier chip). An en dash between two clock
   * readings is a bidi neutral: inside a right-to-left run it takes the
   * paragraph's direction, so «17:00–19:00» rendered «19:00–17:00» — the
   * correct two values in the wrong order, which reads as a window that starts
   * when it ends. Measured, not inferred: the x-position of each reading in an
   * Arabic paragraph, 17:00 painting to the RIGHT of 19:00.
   *
   * A `<bdi>` per endpoint does NOT fix this (it isolates each value and still
   * lets the paragraph order the pair), and there is no element here to carry
   * `dir` — so U+2066…U+2069 around the whole reading is the fix, which is the
   * same rule the VAT clipboard line follows. See the bidi sections in
   * `docs/design/layout-typography-color.md`.
   */
  const hours = isolateForPlainText(
    `${formatMinuteOfDay(window.activeFromMinute)}–${formatMinuteOfDay(window.activeToMinute)}`,
  );
  // A window whose end is before its start runs past midnight (a 22:00–02:00
  // late tier). Said explicitly, because "22:00–02:00" read literally is an
  // empty range and a manager checking their own setup deserves to see that
  // the system read it the way they meant it.
  const wraps = window.activeToMinute < window.activeFromMinute;
  const suffix = wraps ? ` (${t((m) => m.manage.tierWindowOvernight)})` : "";
  return `${days} · ${hours}${suffix}`;
}

/**
 * A tier's resulting schedule, ALWAYS stated (UX-round D54, slice 10.4).
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * `formatTierWindow` above returns `null` for a tier with no window, on the
 * reasoning that a chip reading "every day, all day" beside every ordinary
 * tier is noise. On the tiers LIST that reasoning inverted into the defect the
 * owner called the clearest instance of what this round was convened over:
 *
 * > `settings-tiers.png` shows one row — «خصم المول · 20%». Open it and all
 * > seven day chips are selected and both time fields are empty, so the 20% is
 * > live every hour the restaurant is open. On a 40,000 EGP week that is a
 * > four-figure leak, and the only screen the owner ever looks at does not
 * > mention it.
 *
 * A tier with no window is not "nothing to say". It is **the widest possible
 * schedule**, and it is the one worth saying out loud. So this states the
 * result in every case, in the owner's own words — «كل يوم · طول اليوم ·
 * بتتطبق لوحدها» — and the modal's two paragraphs of midnight-crossing and
 * auto-apply rules stop being the only place the answer exists.
 *
 * It DELEGATES to `formatTierWindow` for every windowed case rather than
 * re-deriving one, which is what keeps the LTR isolate around the hour range:
 * that isolate is the app's only isolation assertion (`tierWindow.spec.ts:107`,
 * `:139`) and a second formatter would be the regression it exists to catch.
 */
export function describeTierSchedule(
  t: Translate,
  tier: TierWindow & { autoApply: boolean },
): string {
  const window = formatTierWindow(t, tier);
  const when =
    window ??
    // The always-on case, said rather than omitted: every day, all day.
    `${t((m) => m.manage.tierWindowEveryDay)} · ${t((m) => m.manage.tierWindowAllDay)}`;
  const how = tier.autoApply
    ? t((m) => m.manage.tierScheduleAutomatic)
    : t((m) => m.manage.tierScheduleManual);
  return `${when} · ${how}`;
}
